import { type FC } from 'react';

import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import StandardFeed from 'layout/Listing/components/StandardFeed';
import usePagination from 'layout/Listing/hooks/usePagination';
import useSaveReturnUrl from 'layout/Listing/hooks/useSaveReturnUrl';
import SearchBar from 'layout/Listing/variants/guides/components/SearchBar';
import useFilterGuides from 'layout/Listing/variants/guides/hooks/useFilteredGuides';
import useGuidesFilterSearchParam from 'layout/Listing/variants/guides/hooks/useGuidesFilterSearchParam';

import { hasArrayValues } from 'utils/array/arrays';
import { mergeListings } from 'utils/array/mergeListings';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import useForceReloadOnBackToResources from 'utils/hooks/useForceReloadOnBackToResources';

import guides from 'data/guides.json';

import type { ListingConfigProps } from 'layout/Listing/config';

const PAGE_SIZE = 12;

type GuideItem = { data: BlogResource };

const GuideListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { newsletterCard, builderState, searchPlaceholder, headingTitle },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<GuideItem>(rawLatestData);

  const localData = toArray<GuideItem>(guides);

  // Merge inteligente: siempre poner TODAS las guides aunque Builder falle
  const guidesList: GuideItem[] = hasArrayValues(latestData) ? mergeListings(latestData, localData) : localData;

  if (!hasArrayValues(guidesList)) {
    console.warn('[GuideListing] No items to render – check builderState/listingData and guides.json');
  }

  useGuidesFilterSearchParam();

  const filteredGuides = useFilterGuides(guidesList).sort();

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination({
    items: filteredGuides,
    pageSize: PAGE_SIZE,
  });

  useSaveReturnUrl('/guides');

  useForceReloadOnBackToResources();

  return (
    <Section {...sectionProps} darkMode={false}>
      <ScrollFadeSection delay={0.1} containPaint={false}>
        <div id="listing-section" className="flex flex-col gap-8">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 sm:max-lg:col-span-full">
              {headingTitle || 'All Guides & Resources'}
            </h2>

            <SearchBar placeholder={searchPlaceholder} />
          </div>

          <StandardFeed posts={paginatedItems} newsletterCard={newsletterCard} />

          {totalItems > PAGE_SIZE && (
            <Pagination
              count={totalItems}
              pageSize={PAGE_SIZE}
              setPage={detail => handlePageChange(detail.page)}
              currentPage={currentPage}
            />
          )}
        </div>
      </ScrollFadeSection>
    </Section>
  );
};

export default GuideListing;
